import { useEffect } from 'react';
import { useTheme } from 'next-themes';

import { useAuthStore } from '../stores/authStore';
import { useBrandingStore } from '../stores/brandingStore';
import { useOrgUiSettingsStore } from '../stores/orgUiSettingsStore';
import organizationService from '../services/organizationService';
import { BRANDING_FONTS, foregroundForHex, hexToHslVar } from '../lib/branding';
import { THEME_PRESETS, buildThemePresetCss, isOliseThemePreset, themePresetFontHref } from '../lib/themePresets';

const PRESET_STYLE_ID = 'org-theme-preset';
const PRESET_FONT_ID = 'org-theme-preset-font';

/** Inject a Google Font stylesheet once per font family. */
function ensureFontLoaded(font: string): void {
  const id = `brand-font-${font.replace(/\s+/g, '-')}`;
  if (document.getElementById(id)) return;
  const family = font.replace(/\s+/g, '+');
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${family}:wght@400;500;600;700&display=swap`;
  document.head.appendChild(link);
}

function isBrandingFont(font: string | null | undefined): font is string {
  return !!font && (BRANDING_FONTS as readonly string[]).includes(font);
}

/**
 * Loads the current organization's branding and applies it to the live document:
 * theme preset (ui_settings.theme_preset), primary color (CSS vars), font
 * family, and the org default light/dark theme.
 *
 * Precedence: Établi defaults (index.css) < theme preset (a <style> whose
 * selectors outrank index.css) < org branding (inline styles on <html>/<body>).
 * With no preset ('default' or unset) nothing is injected, so the branding
 * behaves exactly as before.
 *
 * The org default theme is only applied when the user has not made an explicit
 * theme choice (persisted as `preferences.theme`); a per-user choice always wins.
 *
 * Mount once near the top of the authenticated layout (AppLayout).
 */
export function useApplyBranding(): void {
  const user = useAuthStore((s) => s.user);
  const orgId = user?.organization_id ?? null;
  const userThemePref = (user?.preferences?.theme as string | undefined) ?? undefined;

  const branding = useBrandingStore((s) => s.branding);
  const setBranding = useBrandingStore((s) => s.setBranding);
  const themePreset = useOrgUiSettingsStore((s) => s.settings?.theme_preset ?? null);
  const brandingHasFont = isBrandingFont(branding?.font_family);
  const { setTheme } = useTheme();

  // Fetch branding whenever the active organization changes.
  useEffect(() => {
    let cancelled = false;
    if (!orgId) {
      setBranding(null);
      return;
    }
    organizationService
      .getBranding(orgId)
      .then((b) => {
        if (!cancelled) setBranding(b);
      })
      .catch(() => {
        // Branding is non-critical — fall back to GenieFactory defaults.
        if (!cancelled) setBranding(null);
      });
    return () => {
      cancelled = true;
    };
  }, [orgId, setBranding]);

  // Theme preset → stylesheet + its Google Font, only while that preset is active.
  useEffect(() => {
    const preset = isOliseThemePreset(themePreset) ? THEME_PRESETS[themePreset] : null;
    const existingStyle = document.getElementById(PRESET_STYLE_ID);
    const existingFont = document.getElementById(PRESET_FONT_ID) as HTMLLinkElement | null;
    if (!preset) {
      existingStyle?.remove();
      existingFont?.remove();
      return;
    }
    const withFonts = !brandingHasFont;
    if (withFonts) {
      const href = themePresetFontHref(preset);
      const link = existingFont ?? document.createElement('link');
      if (link.href !== href) link.href = href;
      if (!existingFont) {
        link.id = PRESET_FONT_ID;
        link.rel = 'stylesheet';
        document.head.appendChild(link);
      }
    } else {
      existingFont?.remove();
    }
    const style = existingStyle ?? document.createElement('style');
    style.textContent = buildThemePresetCss(preset, withFonts);
    if (!existingStyle) {
      style.id = PRESET_STYLE_ID;
      document.head.appendChild(style);
    }
  }, [themePreset, brandingHasFont]);

  // Primary accent color → CSS variables (overrides both light & dark tokens).
  useEffect(() => {
    const root = document.documentElement;
    const color = branding?.primary_color;
    const hsl = color ? hexToHslVar(color) : null;
    if (color && hsl) {
      root.style.setProperty('--primary', hsl);
      root.style.setProperty('--ring', hsl);
      root.style.setProperty('--primary-foreground', foregroundForHex(color));
    } else {
      root.style.removeProperty('--primary');
      root.style.removeProperty('--ring');
      root.style.removeProperty('--primary-foreground');
    }
  }, [branding?.primary_color]);

  // Secondary accent color → shadcn --secondary tokens (secondary buttons/badges)
  // plus a dedicated --brand-secondary token for opt-in brand surfaces (links,
  // gradients, second chart series). --brand-secondary is only ever set here, so
  // `hsl(var(--brand-secondary, var(--primary)))` falls back to primary when unset.
  useEffect(() => {
    const root = document.documentElement;
    const color = branding?.secondary_color;
    const hsl = color ? hexToHslVar(color) : null;
    if (color && hsl) {
      root.style.setProperty('--secondary', hsl);
      root.style.setProperty('--secondary-foreground', foregroundForHex(color));
      root.style.setProperty('--brand-secondary', hsl);
    } else {
      root.style.removeProperty('--secondary');
      root.style.removeProperty('--secondary-foreground');
      root.style.removeProperty('--brand-secondary');
    }
  }, [branding?.secondary_color]);

  // Custom font family.
  useEffect(() => {
    const font = branding?.font_family;
    if (isBrandingFont(font)) {
      ensureFontLoaded(font);
      document.body.style.fontFamily = `'${font}', sans-serif`;
    } else {
      document.body.style.removeProperty('font-family');
    }
  }, [branding?.font_family]);

  // Org default theme — applied when the user follows the organization
  // (explicit 'organization' choice, or no per-user choice yet). An explicit
  // light/dark/system choice always wins.
  useEffect(() => {
    const followsOrg = !userThemePref || userThemePref === 'organization';
    if (!followsOrg) return;
    if (!branding?.default_theme) return;
    setTheme(branding.default_theme);
  }, [branding?.default_theme, userThemePref, setTheme]);
}
